/**
 * ResetPassword Page
 * Landing page for password reset email links (/reset-password?token=xxx)
 * Validates token, accepts new password, redirects to home on success
 */
import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const calculateStrength = (pw) => {
    let score = 0;
    if (pw.length >= 8) score += 20;
    if (pw.length >= 12) score += 10;
    if (/[A-Z]/.test(pw)) score += 20;
    if (/[a-z]/.test(pw)) score += 20;
    if (/[0-9]/.test(pw)) score += 15;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pw)) score += 15;
    return Math.min(score, 100);
  };

  const strengthColor = passwordStrength < 30 ? 'bg-red-500' : passwordStrength < 60 ? 'bg-yellow-500' : passwordStrength < 80 ? 'bg-blue-500' : 'bg-green-500';
  const strengthText = passwordStrength < 30 ? 'Weak' : passwordStrength < 60 ? 'Fair' : passwordStrength < 80 ? 'Good' : 'Strong';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_ENDPOINTS.auth}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, confirmPassword })
      });
      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => navigate('/'), 3000);
      } else {
        setError(data.error || data.errors?.password?.join(', ') || 'Reset failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // No token in URL
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center border border-rose-100">
          <span className="text-4xl">⚠️</span>
          <h2 className="text-xl font-bold text-gray-800 mt-4 mb-2">Invalid Reset Link</h2>
          <p className="text-gray-500 text-sm mb-6">This link is missing or malformed. Please request a new password reset.</p>
          <button onClick={() => navigate('/')} className="px-6 py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 transition-all duration-300 transform hover:scale-105 shadow-lg">
            Go to Home 💍
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-white via-rose-50 to-pink-50 rounded-2xl shadow-2xl p-6 w-full max-w-md relative border border-rose-100">
        {/* Top gradient bar */}
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-rose-400 via-pink-400 to-purple-400 rounded-t-2xl"></div>
        <div className="absolute -top-1 -right-1 w-8 h-8 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full opacity-20 animate-bounce"></div>

        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full mb-3 shadow-lg">
            <span className="text-2xl animate-bounce">🔐</span>
          </div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Set New Password
          </h2>
          <p className="text-gray-600 font-medium text-sm">Choose a strong password for your VowAI account</p>
          <div className="w-20 h-1 bg-gradient-to-r from-rose-400 to-pink-400 rounded-full mx-auto mt-2"></div>
        </div>

        {error && (
          <div className="mb-4 p-4 rounded-xl border-l-4 bg-gradient-to-r from-red-50 to-rose-50 border-red-400 text-red-700 shadow-sm">
            <div className="flex items-center">
              <span className="mr-2">⚠️</span>
              {error}
            </div>
          </div>
        )}

        {!success ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <span className="mr-2 animate-pulse">🔒</span>
                New Password
              </label>
              <input
                type="password"
                className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-rose-200 transition-all duration-300 bg-white/80 backdrop-blur-sm border-gray-200 focus:border-rose-400 hover:border-rose-300"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordStrength(calculateStrength(e.target.value)); setError(''); }}
                placeholder="Create a strong password"
                required
              />
              {password && (
                <div className="mt-2 p-2 bg-gradient-to-r from-gray-50 to-rose-50 rounded-lg border border-gray-200">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium text-gray-700">Strength:</span>
                    <span className={`font-bold ${passwordStrength < 60 ? 'text-red-600' : passwordStrength < 80 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {strengthText}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 shadow-inner">
                    <div className={`h-2 rounded-full transition-all duration-500 ${strengthColor} shadow-sm`} style={{ width: `${passwordStrength}%` }}></div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <span className="mr-2 animate-pulse">🔐</span>
                Confirm Password
              </label>
              <input
                type="password"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-rose-200 transition-all duration-300 bg-white/80 backdrop-blur-sm ${
                  confirmPassword && password !== confirmPassword ? 'border-red-400' : 'border-gray-200 focus:border-rose-400 hover:border-rose-300'
                }`}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                onPaste={(e) => e.preventDefault()}
                placeholder="Confirm your password"
                required
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-red-500 text-sm mt-2 flex items-center">
                  <span className="mr-1 animate-bounce">⚠️</span>
                  Passwords do not match
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !password || !confirmPassword || password !== confirmPassword}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 transform shadow-lg ${
                loading || !password || !confirmPassword || password !== confirmPassword
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 hover:scale-105 hover:shadow-xl active:scale-95'
              }`}
            >
              <span className="flex items-center justify-center">
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Resetting...
                  </>
                ) : (
                  <>
                    <span className="mr-2 animate-bounce">✨</span>
                    Reset Password
                  </>
                )}
              </span>
            </button>
          </form>
        ) : (
          <div className="text-center space-y-4 py-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full">
              <span className="text-3xl animate-bounce">✅</span>
            </div>
            <h3 className="text-lg font-bold text-gray-800">Password Reset Successfully!</h3>
            <p className="text-sm text-gray-500">Redirecting you to login...</p>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div className="h-1.5 rounded-full bg-gradient-to-r from-rose-400 to-pink-400 animate-pulse" style={{ width: '100%' }}></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
